import { inject, Injectable } from '@angular/core';
import { catchError, firstValueFrom, forkJoin, map, of } from 'rxjs';
import { CoreService } from './core.service';
import { PortalPageViewModel, SettingsAPIClient, SystemData } from './settings.api.client';
import { PortalPageLink } from './kb-chat-sse.util';

/**
 * Every portal screen the signed-in user can open, across all of their systems, as
 * `{ titles, url }` — the lookup the Ask AI panel uses to link screen names in an answer
 * (`autoLinkPageNames`).
 *
 * The sidebar's page list (`UserAuthService`) only covers the current system, but an answer asked
 * from Inventory often points at a Sales or Accounting screen. So this asks for the user's systems
 * (the same call the header's Systems menu makes) and then each system's authorized pages, once
 * per session. A screen's URL is its system's base path plus the last segment of `PageName` —
 * exactly how the sidebar routes to it inside that system.
 *
 * Best effort: any failure yields fewer (or no) links, never an error in the chat.
 */
@Injectable({ providedIn: 'root' })
export class PortalPageLinksService {
  private readonly settingsApiClient = inject(SettingsAPIClient);
  private readonly appSetting = inject(CoreService);
  private pages: Promise<PortalPageLink[]> | null = null;

  /** Starts loading on first call; later calls share the same result. */
  load(): Promise<PortalPageLink[]> {
    if (!this.pages) {
      this.pages = this.fetch().catch(() => {
        this.pages = null; // let a later answer retry
        return [];
      });
    }
    return this.pages;
  }

  private async fetch(): Promise<PortalPageLink[]> {
    const language = this.appSetting.getLanguage();
    const data = await firstValueFrom(this.settingsApiClient.getUserSystemListWithCompanyData(language === 'ar'));
    const systems = (data.AllSystemsList ?? []).filter(
      (s: SystemData) => s.SystemID && s.Url && s.IsAuthorized !== false && !s.IsExtrenalApp,
    );
    if (systems.length === 0) return [];

    // A system can be authorized for the company but not served on this portal (it 404s), and a
    // link into it would be broken — so only keep its screens if its base path actually answers.
    // Shared screens are the exception: every system serves them, and the panel re-points them at
    // the current system, so they are kept whichever system listed them (on staging, System
    // Reports is only listed under Fina Shops, which isn't served).
    const servedFlags = await Promise.all(systems.map((s) => isServed(systemBase(s))));

    const perSystem = systems.map((system, i) =>
      this.settingsApiClient.getUserAuthorizedPages(system.SystemID, language).pipe(
        map((pages) => toPageLinks(system, pages ?? []).filter((link) => servedFlags[i] || link.shared)),
        catchError(() => of([] as PortalPageLink[])),
      ),
    );
    const results = await firstValueFrom(forkJoin(perSystem));
    return results.flat();
  }
}

/**
 * Screens every system serves at the same route (the header's System Reports icon opens it from
 * anywhere). Marked `shared` so the panel can link the copy in the system the user is in.
 */
const SHARED_SCREEN_ROUTES = new Set(['systemreports']);

function systemBase(system: SystemData): string {
  return '/' + (system.Url ?? '').replace(/^\/+|\/+$/g, '');
}

async function isServed(base: string): Promise<boolean> {
  try {
    const res = await fetch(`${base}/`, { method: 'HEAD', credentials: 'same-origin' });
    return res.ok;
  } catch {
    return false;
  }
}

function toPageLinks(system: SystemData, pages: PortalPageViewModel[]): PortalPageLink[] {
  const base = systemBase(system);
  return pages
    .filter((p) => p.IsActive !== false && p.IsMenuItem !== false && p.PageName && p.PageName !== '#')
    .map((p) => {
      const route = (p.PageName ?? '').split('/').filter(Boolean).pop() ?? '';
      const titles = [p.ResTitle, p.Title, p.DisplayTitle, p.Description].filter((t): t is string => !!t?.trim());
      return { titles, url: route ? `${base}/${route}` : '', shared: SHARED_SCREEN_ROUTES.has(route.toLowerCase()) };
    })
    .filter((link) => link.url && link.titles.length > 0);
}
